// i18n.ts — UI-chrome strings for respondent-facing pages.
// Pack content (blurb, voice_note, prompts, hints) is translated on the server
// via Workers AI; this file only covers the fixed UI strings (Begin, Next,
// Skip this one, QUESTION N OF M, etc.).

import type { Lang } from './api'

type Key =
  | 'loading'
  | 'begin'
  | 'preparing'
  | 'question_n_of_m'
  | 'percent'
  | 'skip_this_one'
  | 'nav_hint'
  | 'go_to_review'
  | 'back'
  | 'next'
  | 'review'
  | 'review_title'
  | 'edit'
  | 'submit'
  | 'submitting'
  | 'saving'
  | 'type_your_answer'
  | 'pick_one'
  | 'final_step'
  | 'how_to_credit'
  | 'consent_note'
  | 'thanks_title'
  | 'thanks_small'
  | 'err_load'
  | 'err_save'
  | 'err_submit'
  | 'language'
  | 'translating'
  | 'voice_message'
  | 'voice_transcribing'
  | 'voice_requesting'
  | 'voice_stop'
  | 'review_eyebrow'
  | 'answered_count'
  | 'tap_to_edit'
  | 'skipped'
  | 'cancel'
  | 'save'
  | 'back_to_start'
  | 'ai_is_listening'
  | 'ai_listening_stage_1'
  | 'ai_listening_stage_2'
  | 'ai_listening_stage_3'
  | 'ai_notices'
  | 'followup_of'
  | 'answer_this'
  | 'skip_next_question'
  | 'save_followup'
  | 'skip_instead'
  | 'followup_placeholder'
  | 'landing_meta'
  | 'begin_continue'
  | 'begin_done'
  | 'load_failed_generic'
  | 'wrong_question'
  | 'public_rate_limited'
  | 'invalid_link'
  | 'invalid_link_body'
  | 'mic_aria'
  | 'not_found_title'
  | 'not_found_body'
  | 'saved_prefix'
  | 'just_now'

type Dict = Record<Key, string>

const en: Dict = {
  loading: 'Loading…',
  begin: 'Begin',
  preparing: 'Preparing…',
  question_n_of_m: 'Question {n} of {m}',
  percent: '{p}%',
  skip_this_one: 'Skip this one',
  nav_hint: '← → to navigate · Esc to review',
  go_to_review: 'Go to review',
  back: 'Back',
  next: 'Next',
  review: 'Review',
  review_title: 'Review your answers',
  edit: 'Edit',
  submit: 'Submit',
  submitting: 'Submitting…',
  saving: 'Saving…',
  type_your_answer: 'Type your answer…',
  pick_one: 'Pick one',
  final_step: 'Final step',
  how_to_credit: 'How should I credit you?',
  consent_note:
    'Anything I plan to quote I\'ll send back before print. You can stay anonymous, give me just enough to colour the voice, or be fully named.',
  thanks_title: 'That\'s it — your answers are in.',
  thanks_small: 'Thank you',
  err_load: 'Couldn\'t load the interview.',
  err_save: 'Couldn\'t save — please try again.',
  err_submit: 'Couldn\'t submit — please try again.',
  language: 'Language',
  translating: 'Translating…',
  voice_message: 'or leave a voice message',
  voice_transcribing: 'Transcribing…',
  voice_requesting: 'Mic…',
  voice_stop: 'Stop',
  review_eyebrow: 'Review',
  answered_count: '{n} of {m} answered',
  tap_to_edit: 'tap any row to edit',
  skipped: 'Skipped',
  cancel: 'Cancel',
  save: 'Save',
  back_to_start: 'Back to start',
  ai_is_listening: 'AI is reading',
  ai_listening_stage_1: 'Reading your answer…',
  ai_listening_stage_2: 'Looking at what you just said…',
  ai_listening_stage_3: 'Thinking it over…',
  ai_notices: 'AI notices',
  followup_of: 'follow-up {n} of {m}',
  answer_this: 'Answer this',
  skip_next_question: 'Skip — next question',
  save_followup: 'Save follow-up',
  skip_instead: 'Skip instead',
  followup_placeholder: 'A sentence or two is enough…',
  landing_meta: '{n} questions · you can skip any · answers save as you go',
  begin_continue: 'Continue',
  begin_done: 'View thank-you',
  load_failed_generic: 'Something went wrong loading this page.',
  wrong_question: "That question isn't part of this pack.",
  public_rate_limited: 'You already filled this survey today. Come back tomorrow.',
  invalid_link: "This link isn't valid.",
  invalid_link_body: 'Either the token is wrong or it has been revoked. If you were expecting to take part, drop Stas a line.',
  mic_aria: 'Start voice recording',
  not_found_title: 'Page not found',
  not_found_body: 'Not the page you were looking for.',
  saved_prefix: 'Saved',
  just_now: 'just now',
}

const pl: Dict = {
  loading: 'Ładowanie…',
  begin: 'Zacznij',
  preparing: 'Przygotowuję…',
  question_n_of_m: 'Pytanie {n} z {m}',
  percent: '{p}%',
  skip_this_one: 'Pomiń to pytanie',
  nav_hint: '← → nawigacja · Esc podgląd',
  go_to_review: 'Przejdź do podglądu',
  back: 'Wstecz',
  next: 'Dalej',
  review: 'Podgląd',
  review_title: 'Sprawdź swoje odpowiedzi',
  edit: 'Edytuj',
  submit: 'Wyślij',
  submitting: 'Wysyłanie…',
  saving: 'Zapisuję…',
  type_your_answer: 'Wpisz odpowiedź…',
  pick_one: 'Wybierz',
  final_step: 'Ostatni krok',
  how_to_credit: 'Jak Cię podpisać?',
  consent_note:
    'Wszystko, co chcę zacytować, prześlę Ci przed drukiem. Możesz zostać anonimowy, zostawić tylko tyle, by głos miał barwę, albo wystąpić pod pełnym imieniem.',
  thanks_title: 'Gotowe — odpowiedzi zapisane.',
  thanks_small: 'Dziękuję',
  err_load: 'Nie udało się załadować wywiadu.',
  err_save: 'Nie udało się zapisać — spróbuj ponownie.',
  err_submit: 'Nie udało się wysłać — spróbuj ponownie.',
  language: 'Język',
  translating: 'Tłumaczę…',
  voice_message: 'albo nagraj wiadomość głosową',
  voice_transcribing: 'Transkrybuję…',
  voice_requesting: 'Mikrofon…',
  voice_stop: 'Zatrzymaj',
  review_eyebrow: 'Podgląd',
  answered_count: 'Odpowiedzi: {n} z {m}',
  tap_to_edit: 'dotknij wiersza, by edytować',
  skipped: 'Pominięto',
  cancel: 'Anuluj',
  save: 'Zapisz',
  back_to_start: 'Wróć na początek',
  ai_is_listening: 'AI czyta',
  ai_listening_stage_1: 'Czytam odpowiedź…',
  ai_listening_stage_2: 'Przyglądam się temu, co napisałeś…',
  ai_listening_stage_3: 'Zastanawiam się…',
  ai_notices: 'AI zauważa',
  followup_of: 'pytanie dodatkowe {n} z {m}',
  answer_this: 'Odpowiedz',
  skip_next_question: 'Pomiń — następne pytanie',
  save_followup: 'Zapisz',
  skip_instead: 'Pomiń jednak',
  followup_placeholder: 'Wystarczy zdanie lub dwa…',
  landing_meta: 'Pytań: {n} · możesz pominąć każde · odpowiedzi zapisują się na bieżąco',
  begin_continue: 'Wróć do wywiadu',
  begin_done: 'Zobacz podziękowanie',
  load_failed_generic: 'Coś poszło nie tak przy ładowaniu strony.',
  wrong_question: 'Tego pytania nie ma w tym zestawie.',
  public_rate_limited: 'Odpowiedziałeś już dzisiaj. Wróć jutro.',
  invalid_link: 'Ten link jest nieważny.',
  invalid_link_body: 'Albo token jest błędny, albo został unieważniony. Jeśli miałeś brać udział — daj znać Stasowi.',
  mic_aria: 'Nagraj odpowiedź głosem',
  not_found_title: 'Nie znaleziono strony',
  not_found_body: 'To nie ta strona, której szukałeś.',
  saved_prefix: 'Zapisano',
  just_now: 'przed chwilą',
}

const uk: Dict = {
  loading: 'Завантаження…',
  begin: 'Почати',
  preparing: 'Готую…',
  question_n_of_m: 'Питання {n} з {m}',
  percent: '{p}%',
  skip_this_one: 'Пропустити',
  nav_hint: '← → навігація · Esc перегляд',
  go_to_review: 'До перегляду',
  back: 'Назад',
  next: 'Далі',
  review: 'Перегляд',
  review_title: 'Перевірте відповіді',
  edit: 'Редагувати',
  submit: 'Надіслати',
  submitting: 'Надсилаю…',
  saving: 'Зберігаю…',
  type_your_answer: 'Введіть відповідь…',
  pick_one: 'Оберіть',
  final_step: 'Останній крок',
  how_to_credit: 'Як вас підписати?',
  consent_note:
    'Усе, що планую цитувати, покажу вам перед друком. Можете лишитись анонімно, дати трішки для забарвлення голосу, або виступити під повним іменем.',
  thanks_title: 'Готово — відповіді збережено.',
  thanks_small: 'Дякую',
  err_load: 'Не вдалося завантажити інтерв\'ю.',
  err_save: 'Не вдалося зберегти — спробуйте ще раз.',
  err_submit: 'Не вдалося надіслати — спробуйте ще раз.',
  language: 'Мова',
  translating: 'Перекладаю…',
  voice_message: 'або залишіть голосове',
  voice_transcribing: 'Розшифровую…',
  voice_requesting: 'Мікрофон…',
  voice_stop: 'Зупинити',
  review_eyebrow: 'Перегляд',
  answered_count: 'Відповіли: {n} з {m}',
  tap_to_edit: 'торкніться рядка для редагування',
  skipped: 'Пропущено',
  cancel: 'Скасувати',
  save: 'Зберегти',
  back_to_start: 'На початок',
  ai_is_listening: 'AI читає',
  ai_listening_stage_1: 'Читаю вашу відповідь…',
  ai_listening_stage_2: 'Дивлюсь на те, що ви написали…',
  ai_listening_stage_3: 'Обдумую…',
  ai_notices: 'AI помічає',
  followup_of: 'уточнення {n} з {m}',
  answer_this: 'Відповісти',
  skip_next_question: 'Пропустити — далі',
  save_followup: 'Зберегти',
  skip_instead: 'Все-таки пропустити',
  followup_placeholder: 'Вистачить одного-двох речень…',
  landing_meta: 'Питань: {n} · будь-яке можна пропустити · відповіді зберігаються на ходу',
  begin_continue: 'Продовжити',
  begin_done: 'Подивитись подяку',
  load_failed_generic: 'Щось пішло не так при завантаженні сторінки.',
  wrong_question: 'Цього питання немає у наборі.',
  public_rate_limited: 'Ви вже заповнили сьогодні. Приходьте завтра.',
  invalid_link: 'Посилання недійсне.',
  invalid_link_body: 'Або токен неправильний, або його скасовано. Якщо ви очікували брати участь — напишіть Стасу.',
  mic_aria: 'Записати відповідь голосом',
  not_found_title: 'Сторінку не знайдено',
  not_found_body: 'Не та сторінка, яку ви шукали.',
  saved_prefix: 'Збережено',
  just_now: 'щойно',
}

const ru: Dict = {
  loading: 'Загрузка…',
  begin: 'Начать',
  preparing: 'Готовлю…',
  question_n_of_m: 'Вопрос {n} из {m}',
  percent: '{p}%',
  skip_this_one: 'Пропустить',
  nav_hint: '← → навигация · Esc просмотр',
  go_to_review: 'К просмотру',
  back: 'Назад',
  next: 'Далее',
  review: 'Просмотр',
  review_title: 'Проверьте ответы',
  edit: 'Изменить',
  submit: 'Отправить',
  submitting: 'Отправляю…',
  saving: 'Сохраняю…',
  type_your_answer: 'Введите ответ…',
  pick_one: 'Выберите',
  final_step: 'Последний шаг',
  how_to_credit: 'Как вас подписать?',
  consent_note:
    'Всё, что планирую цитировать, покажу вам перед печатью. Можете остаться анонимно, дать немного для окраски голоса или выступить под полным именем.',
  thanks_title: 'Готово — ответы сохранены.',
  thanks_small: 'Спасибо',
  err_load: 'Не удалось загрузить интервью.',
  err_save: 'Не удалось сохранить — попробуйте ещё раз.',
  err_submit: 'Не удалось отправить — попробуйте ещё раз.',
  language: 'Язык',
  translating: 'Перевожу…',
  voice_message: 'или запишите голосовое',
  voice_transcribing: 'Расшифровываю…',
  voice_requesting: 'Микрофон…',
  voice_stop: 'Стоп',
  review_eyebrow: 'Просмотр',
  answered_count: 'Ответили: {n} из {m}',
  tap_to_edit: 'нажмите на строку, чтобы изменить',
  skipped: 'Пропущено',
  cancel: 'Отмена',
  save: 'Сохранить',
  back_to_start: 'К началу',
  ai_is_listening: 'AI читает',
  ai_listening_stage_1: 'Читаю ваш ответ…',
  ai_listening_stage_2: 'Вчитываюсь в то, что вы написали…',
  ai_listening_stage_3: 'Обдумываю…',
  ai_notices: 'AI замечает',
  followup_of: 'уточнение {n} из {m}',
  answer_this: 'Ответить',
  skip_next_question: 'Пропустить — дальше',
  save_followup: 'Сохранить',
  skip_instead: 'Всё же пропустить',
  followup_placeholder: 'Хватит одного-двух предложений…',
  landing_meta: 'Вопросов: {n} · любой можно пропустить · ответы сохраняются на ходу',
  begin_continue: 'Продолжить',
  begin_done: 'Посмотреть благодарность',
  load_failed_generic: 'Что-то пошло не так при загрузке страницы.',
  wrong_question: 'Такого вопроса в этом наборе нет.',
  public_rate_limited: 'Вы уже заполнили сегодня. Приходите завтра.',
  invalid_link: 'Ссылка недействительна.',
  invalid_link_body: 'Либо токен неверный, либо его отозвали. Если вы ожидали участвовать — напишите Стасу.',
  mic_aria: 'Записать ответ голосом',
  not_found_title: 'Страница не найдена',
  not_found_body: 'Не та страница, которую вы искали.',
  saved_prefix: 'Сохранено',
  just_now: 'только что',
}

const es: Dict = {
  loading: 'Cargando…',
  begin: 'Empezar',
  preparing: 'Preparando…',
  question_n_of_m: 'Pregunta {n} de {m}',
  percent: '{p}%',
  skip_this_one: 'Saltar esta',
  nav_hint: '← → navegar · Esc revisar',
  go_to_review: 'Ir a la revisión',
  back: 'Atrás',
  next: 'Siguiente',
  review: 'Revisar',
  review_title: 'Revisa tus respuestas',
  edit: 'Editar',
  submit: 'Enviar',
  submitting: 'Enviando…',
  saving: 'Guardando…',
  type_your_answer: 'Escribe tu respuesta…',
  pick_one: 'Elige una',
  final_step: 'Último paso',
  how_to_credit: '¿Cómo debería citarte?',
  consent_note:
    'Cualquier cita que planee usar te la enviaré antes de imprimir. Puedes quedarte en anónimo, dar lo justo para dar color a la voz, o aparecer con nombre completo.',
  thanks_title: 'Listo — tus respuestas están enviadas.',
  thanks_small: 'Gracias',
  err_load: 'No se pudo cargar la entrevista.',
  err_save: 'No se pudo guardar — inténtalo de nuevo.',
  err_submit: 'No se pudo enviar — inténtalo de nuevo.',
  language: 'Idioma',
  translating: 'Traduciendo…',
  voice_message: 'o deja una nota de voz',
  voice_transcribing: 'Transcribiendo…',
  voice_requesting: 'Micro…',
  voice_stop: 'Parar',
  review_eyebrow: 'Revisión',
  answered_count: '{n} de {m} respondidas',
  tap_to_edit: 'toca una fila para editar',
  skipped: 'Omitida',
  cancel: 'Cancelar',
  save: 'Guardar',
  back_to_start: 'Al inicio',
  ai_is_listening: 'La IA lee',
  ai_listening_stage_1: 'Leyendo tu respuesta…',
  ai_listening_stage_2: 'Mirando lo que escribiste…',
  ai_listening_stage_3: 'Pensándolo…',
  ai_notices: 'La IA nota algo',
  followup_of: 'seguimiento {n} de {m}',
  answer_this: 'Responder',
  skip_next_question: 'Saltar — siguiente pregunta',
  save_followup: 'Guardar',
  skip_instead: 'Saltar igualmente',
  followup_placeholder: 'Con una o dos frases basta…',
  landing_meta: 'Preguntas: {n} · puedes saltar cualquiera · las respuestas se guardan solas',
  begin_continue: 'Continuar',
  begin_done: 'Ver agradecimiento',
  load_failed_generic: 'Algo salió mal al cargar la página.',
  wrong_question: 'Esa pregunta no pertenece a este pack.',
  public_rate_limited: 'Ya respondiste hoy. Vuelve mañana.',
  invalid_link: 'Este enlace no es válido.',
  invalid_link_body: 'O el token es incorrecto, o ha sido revocado. Si esperabas participar, escríbele a Stas.',
  mic_aria: 'Grabar respuesta por voz',
  not_found_title: 'Página no encontrada',
  not_found_body: 'No es la página que buscabas.',
  saved_prefix: 'Guardado',
  just_now: 'ahora mismo',
}

const DICTS: Record<Lang, Dict> = { en, pl, uk, ru, es }

export function t(lang: Lang, key: Key, params?: Record<string, string | number>): string {
  const d = DICTS[lang] ?? DICTS.en
  let s = d[key] ?? en[key]
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.split(`{${k}}`).join(String(v))
    }
  }
  return s
}

export type { Key as I18nKey }
